/**
 * File: useADRAutoSave.ts
 * Description: Custom hook to handle auto-save functionality for ADR content
 * Author: GitHub Copilot
 * Created: 2025-01-XX
 * Last Modified: 2025-01-XX
 */

import { useEffect } from 'react';

import { ADRDefinition } from '../../types/adr';

// Constants
const AUTO_SAVE_INTERVAL_MS = 30000; // 30 seconds

interface UseADRAutoSaveProps {
  hasUnsavedChanges: boolean;
  editingADR: ADRDefinition | null;
  shouldSave: (_hasChanges: boolean, _adr: ADRDefinition | null) => boolean;
  onAutoSave: () => void;
}

/**
 * Hook to handle auto-save of ADR content every 30 seconds
 */
export const useADRAutoSave = ({
  hasUnsavedChanges,
  editingADR,
  shouldSave,
  onAutoSave,
}: UseADRAutoSaveProps): void => {
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (shouldSave(hasUnsavedChanges, editingADR)) {
        onAutoSave();
      }
    }, AUTO_SAVE_INTERVAL_MS);

    return () => clearInterval(autoSaveInterval);
  }, [hasUnsavedChanges, editingADR, shouldSave, onAutoSave]);
};
